import { connect } from 'react-redux';
import { useCallback, useMemo, useState } from 'react';
import { FieldArray } from 'react-final-form-arrays';
import { v4 as uuidv4 } from 'uuid';
import { Field, useField } from 'react-final-form';

import { IKnmErknmFormReason } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormReason';
import { IKnmFormRef } from 'KnmPackage/MainModule/interfaces/IKnmFormRef';
import {
  getErknmKnmRiskIndicatorsSelectOptions,
  getKnmDataCreated,
} from 'KnmPackage/MainModule/selectors/knmSelectors';
import { SelectInputFieldWithDescription } from 'UIPackage/MainModule/components/SelectInputField';
import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { IRiskIndicatory } from 'APIPackage/DictionariesModule/interfaces/IRiskIndicatory';
import { IDictionarySelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionarySelectOption';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { isLaterThanAdminSetting } from 'CorePackage/MainModule/validators/isLaterThanAdminSetting';
import { AdminSettingCodesEnum } from 'APIPackage/AdminModule/enums/AdminSettingCodesEnum';
import { getAdminFetchSettings } from 'PersonalAccountPackage/AdminModule/selectors/adminSelectors';
import { IAdminSetting } from 'APIPackage/AdminModule/interfaces/IAdminSetting';
import { IDecisionFormReason } from 'DecisionPackage/MainModule/interfaces/IDecisionFormReason';
import { getDecisionRiskIndicatorsSelectOptions } from 'DecisionPackage/MainModule/selectors/decisionSelectors';
import {
  getDecisionRiskIndicatorBlockId,
  getDecisionRiskIndicatorsBlockId,
} from 'CorePackage/MainModule/routes/routes';
import { IDecisionFormRef } from 'DecisionPackage/MainModule/interfaces/IDecisionFormRef';

import { KnmRiskIndicatorDocument } from './KnmRiskIndicatorDocument';
import { CheckboxField } from '../../../UIPackage/MainModule/components/CheckboxField';
import {
  getReasonRiskIndicatorBlockId,
  getReasonRiskIndicatorsBlockId,
} from '../../../KnmErknmPackage/MainModule/constants/knmErknmFieldsElementsIds';
import { Button } from '../../../UIPackage/MainModule/components/Button';
import { Row } from '../../../UIPackage/FormModule/components/Row';
import { KnmCollapse } from '../../MainModule/components/KnmCollapse';
import { IKnmFormRiskIndicators } from '../../MainModule/interfaces/IKnmFormRiskIndicators';
import {
  getRiskIndicatorBlockId,
  getRiskIndicatorsBlockId,
} from '../../../KnmPmPackage/MainModule/constants/knmPmFieldsElementsIds';
import { getTitleFieldWithRedStar } from '../../../functions/getTitleFieldWithRedStar';
import { checkHasErrorInCollapseBlock } from '../../../functions/checkHasErrorInCollapseBlock';

interface IKnmReasonRiskIndicatorsProps {
  isErp?: boolean;
  isPm?: boolean;
  isDecision?: boolean;
  isWatchOnly: boolean;
  reasonFieldValue: IKnmErknmFormReason | IDecisionFormReason;
  reasonsFieldName: string;
  formRef: IKnmFormRef | IDecisionFormRef | undefined;
}

interface IKnmReasonRiskIndicatorsStoreProps {
  created: string | null | undefined;
  riskIndicatorsSelectOptions: IDictionarySelectOption<IRiskIndicatory>[] | undefined;
  riskIndicatorsSelectOptionsDecision: IDictionarySelectOption<IRiskIndicatory>[] | undefined;
  settings: IAdminSetting[] | undefined;
}

const KnmReasonRiskIndicatorsWithoutStore = ({
  isErp,
  isPm,
  isDecision,
  isWatchOnly,
  reasonFieldValue,
  reasonsFieldName,
  created,
  formRef,
  riskIndicatorsSelectOptions,
  riskIndicatorsSelectOptionsDecision,
  settings,
}: IKnmReasonRiskIndicatorsProps & IKnmReasonRiskIndicatorsStoreProps) => {
  const [knmCollapseOpened, setKnmCollapseOpened] = useState<{ [key: string]: boolean }>({});
  const riskIndicatorsFieldName = `${reasonsFieldName}.riskIndicators`;
  const push = formRef?.current.mutators.push;
  const remove = formRef?.current.mutators.remove;
  const riskIndicators: IKnmFormRiskIndicators[] | '' | undefined = useField(riskIndicatorsFieldName, {
    subscription: { value: true },
  }).input.value;

  const isUseRiskIndicatorShortTitle = isLaterThanAdminSetting(
    created,
    settings,
    AdminSettingCodesEnum.USE_RISK_INDICATOR_SHORT_TITLE_STARTS_FROM,
  );

  const riskIndicatorsFormattedSelectOptions = useMemo((): IDictionarySelectOption<IRiskIndicatory>[] | undefined => {
    const selectedDictVersionIds = Array.isArray(riskIndicators)
      ? riskIndicators.map(item => item.riskIndicator?.value.dictVersionId).filter(item => !!item)
      : [];
    const notSelectedOptions = (isDecision ? riskIndicatorsSelectOptionsDecision : riskIndicatorsSelectOptions)?.filter(
      item => !selectedDictVersionIds.includes(item.value.dictVersionId),
    );

    if (isUseRiskIndicatorShortTitle) {
      return notSelectedOptions?.map(item => ({
        ...item,
        label: item.shortTitle || item.title,
      }));
    }

    return notSelectedOptions;
  }, [isUseRiskIndicatorShortTitle, riskIndicatorsSelectOptions, riskIndicators, isDecision]);

  const addReasonRiskIndicator = useCallback(() => {
    if (!isWatchOnly && push) {
      const addingRiskIndicator: IKnmFormRiskIndicators = {
        guid: uuidv4(),
        indicatorRiskAttachment: undefined,
        indicatorRiskAuto: undefined,
        riskIndicator: undefined,
        isNew: true,
      };

      push(riskIndicatorsFieldName, addingRiskIndicator);
    }
  }, [push, isWatchOnly, riskIndicatorsFieldName]);

  const makeRemoveHandler = (index: number) => () => {
    if (!isWatchOnly && remove) {
      remove(riskIndicatorsFieldName, index);
    }
  };

  const getMainFieldBlockId = useCallback(
    (id: string) => {
      if (isDecision) {
        return getDecisionRiskIndicatorsBlockId(id);
      }

      if (isPm) {
        return getRiskIndicatorsBlockId(id);
      }

      if (isErp) {
        return `reasonsErvk[${id}].riskIndicators`;
      }

      return getReasonRiskIndicatorsBlockId(id);
    },
    [isDecision, isPm, isErp],
  );

  const getInnerFieldBlockId = useCallback(
    (id: string, idInner: string) => {
      if (isDecision) {
        return getDecisionRiskIndicatorBlockId(id, idInner);
      }

      if (isPm) {
        return getRiskIndicatorBlockId(id, idInner);
      }

      if (isErp) {
        return `reasonsErvk[${id}].riskIndicators[${idInner}]`;
      }

      return getReasonRiskIndicatorBlockId(id, idInner);
    },
    [isDecision, isPm, isErp],
  );

  return (
    <FieldBlock
      label={getTitleFieldWithRedStar({
        label: 'Индикаторы риска',
        field: riskIndicatorsFieldName,
        isPm,
        isDecision,
        isKnm: !isPm && !isDecision,
      })}
      id={getMainFieldBlockId(reasonFieldValue.listId || '')}
      margin="top"
      button={
        <Button
          className="shared-btn-add"
          text="Добавить"
          type="button"
          variant="link"
          color="primary"
          onClick={addReasonRiskIndicator}
          disabled={isWatchOnly}
        />
      }
    >
      <>
        <Field name={riskIndicatorsFieldName}>
          {({ meta }) => {
            if (meta.touched && typeof meta.error === 'string') {
              return <p className="shared-input-error shared-row">{meta.error}</p>;
            }

            return null;
          }}
        </Field>
        <FieldArray name={riskIndicatorsFieldName}>
          {({ fields }) => {
            if (!fields.length) {
              return null;
            }

            return (
              <Row>
                {fields.map((fieldName, sIndex) => {
                  const fieldValue: IKnmFormRiskIndicators = fields.value?.[sIndex];
                  const id = getInnerFieldBlockId(reasonFieldValue.listId || '', fieldValue.guid);

                  const hasError = checkHasErrorInCollapseBlock(formRef, fieldName);

                  return (
                    <KnmCollapse
                      id={id}
                      key={id}
                      theme="fields"
                      title={`Индикатор риска №${sIndex + 1}`}
                      onClose={!isWatchOnly ? makeRemoveHandler(sIndex) : undefined}
                      openByDefault={fieldValue.isNew}
                      changeOpenedCallback={e =>
                        setKnmCollapseOpened(state => ({
                          ...state,
                          [fieldName]: e,
                        }))
                      }
                      error={hasError && knmCollapseOpened[fieldName]}
                    >
                      <>
                        <SelectInputFieldWithDescription
                          disabled={isWatchOnly}
                          id={`${id}.riskIndicator`}
                          isShowTooltip={isUseRiskIndicatorShortTitle && !!fieldValue.riskIndicator?.shortTitle}
                          name={`${fieldName}.riskIndicator`}
                          options={riskIndicatorsFormattedSelectOptions}
                          placeholder={getTitleFieldWithRedStar({
                            label: 'Выберите индикатор риска',
                            field: `${fieldName}.riskIndicator`,
                            isPm,
                            isDecision,
                            isKnm: !isPm && !isDecision,
                          })}
                          size="medium"
                        />
                        <FieldBlock margin="top">
                          <CheckboxField
                            disabled={isWatchOnly}
                            id={`${id}.indicatorRiskAuto`}
                            label={'Индикатор срабатывает в автоматическом режиме'}
                            name={`${fieldName}.indicatorRiskAuto`}
                            size="medium"
                            theme="system"
                            type="checkbox"
                          />
                        </FieldBlock>
                        {reasonFieldValue?.type?.indicatorRiskRequiredFile && (
                          <FieldBlock margin="top" id={`${id}.indicatorRiskAttachment`}>
                            <KnmRiskIndicatorDocument
                              disableToAdd={
                                isDecision ? !!fieldValue.indicatorRiskAttachment?.length || isWatchOnly : isWatchOnly
                              }
                              acceptedFiles={isDecision ? '.pdf,.png,.jpeg,.rtf,.zip,.rar' : undefined}
                              name={`${fieldName}.indicatorRiskAttachment`}
                              formRef={formRef}
                              watchOnly={isWatchOnly}
                              title={
                                <>
                                  {getTitleFieldWithRedStar({
                                    label: 'Файл',
                                    field: `${fieldName}.indicatorRiskAttachment`,
                                    isPm,
                                    isDecision,
                                    isKnm: !isPm && !isDecision,
                                  })}
                                </>
                              }
                            />
                          </FieldBlock>
                        )}
                      </>
                    </KnmCollapse>
                  );
                })}
              </Row>
            );
          }}
        </FieldArray>
      </>
    </FieldBlock>
  );
};

const mapStateToProps = (state: IRootState): IKnmReasonRiskIndicatorsStoreProps => ({
  created: getKnmDataCreated(state),
  riskIndicatorsSelectOptions: getErknmKnmRiskIndicatorsSelectOptions(state),
  riskIndicatorsSelectOptionsDecision: getDecisionRiskIndicatorsSelectOptions(state),
  settings: getAdminFetchSettings(state),
});

export const KnmReasonRiskIndicators = connect(mapStateToProps)(KnmReasonRiskIndicatorsWithoutStore);
